import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FiArrowLeft as ArrowLeft,
  FiRefreshCw as RefreshCw,
  FiTrash2 as Trash2,
  FiCopy as Copy,
  FiDatabase as Database,
  FiCloud as Cloud,
  FiHash as Hash,
  FiClock as Clock,
  FiCheckCircle as CheckCircle,
  FiAlertTriangle as AlertTriangle,
  FiCode as Code,
  FiTerminal as Terminal,
  FiExternalLink as ExternalLink
} from 'react-icons/fi';
import { useVectorExport } from '../../../../hooks/useVectorExports';
import { getDataset } from '../../../../services/datasetsApi';
import { apiCall } from '../../../../services/api';
import { ConfirmDialog } from '../../../../components/ui';
import ProjectAssignmentSelect from '../../../../components/ProjectAssignmentSelect';
import WorkloadAssignmentSelect from '../../../../components/WorkloadAssignmentSelect';
import { assignVectorExportProject, assignVectorExportWorkload } from '../../../../services/vectorExportsApi';

const VectorExportDetailPage = () => {
  const { exportId } = useParams<{ exportId: string }>();
  const navigate = useNavigate();
  const { vectorExport, usageExample, loading, syncing, error, sync, remove } = useVectorExport(exportId);

  const [dataset, setDataset] = useState<Record<string, any> | null>(null);
  const [awsAccount, setAwsAccount] = useState<Record<string, any> | null>(null);
  const [activeTab, setActiveTab] = useState('python');
  const [copied, setCopied] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [assignedProjectId, setAssignedProjectId] = useState<string | null>(null);
  const [assignedWorkloadId, setAssignedWorkloadId] = useState<string | null>(null);

  // Delete dialog state
  const [deleteDialog, setDeleteDialog] = useState<{ isOpen: boolean; step: string }>({ isOpen: false, step: 'aws' });
  const [deleteAwsResources, setDeleteAwsResources] = useState(false);

  useEffect(() => {
    const loadRelatedData = async () => {
      if (vectorExport) {
        setAssignedProjectId(vectorExport.project_id ?? null);
        setAssignedWorkloadId(vectorExport.workload_id ?? null);
        try {
          const [datasetData, accountsData] = await Promise.all([
            getDataset(vectorExport.dataset_id),
            apiCall('/aws/accounts')
          ]);
          setDataset(datasetData);
          const account = (accountsData as { id: string; account_id?: string; [key: string]: unknown }[]).find((a) => a.id === vectorExport.aws_account_id);
          setAwsAccount(account || null);
        } catch (err: unknown) {
          console.error('Error loading related data:', err);
        }
      }
    };
    loadRelatedData();
  }, [vectorExport]);

  const handleSync = async () => {
    try {
      await sync();
    } catch (err: unknown) {
      alert((err instanceof Error ? err.message : String(err)) || 'Failed to sync');
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialog({ isOpen: true, step: 'aws' });
    setDeleteAwsResources(false);
  };

  const handleAwsResourcesChoice = (deleteAws: boolean) => {
    setDeleteAwsResources(deleteAws);
    setDeleteDialog(prev => ({ ...prev, step: 'confirm' }));
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await remove(deleteAwsResources);
      navigate('/ai-operations/registry?tab=vector-exports');
    } catch (err: unknown) {
      alert((err instanceof Error ? err.message : String(err)) || 'Failed to delete');
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ isOpen: false, step: 'aws' });
    setDeleteAwsResources(false);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { bg: string; text: string }> = {
      pending: { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-800 dark:text-gray-300' },
      in_progress: { bg: 'bg-blue-100 dark:bg-blue-900/50', text: 'text-blue-800 dark:text-blue-300' },
      completed: { bg: 'bg-green-100 dark:bg-green-900/50', text: 'text-green-800 dark:text-green-300' },
      failed: { bg: 'bg-red-100 dark:bg-red-900/50', text: 'text-red-800 dark:text-red-300' },
      syncing: { bg: 'bg-yellow-100 dark:bg-yellow-900/50', text: 'text-yellow-800 dark:text-yellow-300' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-full ${config.bg} ${config.text}`}>
        {status === 'completed' && <CheckCircle className="w-4 h-4" />}
        {status === 'failed' && <AlertTriangle className="w-4 h-4" />}
        {(status === 'in_progress' || status === 'syncing') && <RefreshCw className="w-4 h-4 animate-spin" />}
        {status}
      </span>
    );
  };

  const formatTimestamp = (timestamp: string | null | undefined) => {
    if (!timestamp) return 'Never';
    return new Date(timestamp).toLocaleString();
  };

  if (loading) {
    return (
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error || !vectorExport) {
    return (
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
            <p className="text-red-800 dark:text-red-300">{error || 'Export not found'}</p>
          </div>
        </div>
      </div>
    );
  }

  // Generate usage examples
  const pythonCode = usageExample?.python_code || `import boto3

# Initialize S3 Vectors client
s3vectors = boto3.client('s3vectors', region_name='${vectorExport.aws_region}')

# Query vectors (example with your own embedding)
response = s3vectors.query_vectors(
    vectorBucketName='${vectorExport.vector_bucket_name}',
    indexName='${vectorExport.vector_index_name}',
    queryVector=[0.1, 0.2, ...],  # Your query embedding (1024 dimensions)
    topK=5
)

for result in response['results']:
    print(f"Key: {result['key']}, Score: {result['score']}")
    print(f"Metadata: {result.get('metadata', {})}")
`;

  const cliCode = usageExample?.cli_command || `# List vectors in the index
aws s3vectors list-vectors \\
  --vector-bucket-name ${vectorExport.vector_bucket_name} \\
  --index-name ${vectorExport.vector_index_name} \\
  --region ${vectorExport.aws_region}

# Query vectors
aws s3vectors query-vectors \\
  --vector-bucket-name ${vectorExport.vector_bucket_name} \\
  --index-name ${vectorExport.vector_index_name} \\
  --query-vector "[0.1, 0.2, ...]" \\
  --top-k 5 \\
  --region ${vectorExport.aws_region}
`;

  const bedrockKbCode = usageExample?.bedrock_kb_integration || `# Bedrock Knowledge Base Integration

To use these vectors with Amazon Bedrock Knowledge Bases:

1. Create a Knowledge Base in AWS Bedrock console
2. Select "S3 Vectors" as the vector store
3. Use these connection details:
   - Bucket: ${vectorExport.vector_bucket_name}
   - Index: ${vectorExport.vector_index_name}
   - Region: ${vectorExport.aws_region}

4. The vectors are already embedded with Amazon Titan V2 (1024 dimensions)
   which is compatible with Bedrock Knowledge Bases.

Note: Make sure your Bedrock service role has access to the S3 Vectors bucket.
`;

  const getTabCode = () => {
    switch (activeTab) {
      case 'python': return pythonCode;
      case 'cli': return cliCode;
      case 'bedrock': return bedrockKbCode;
      default: return pythonCode;
    }
  };

  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/ai-operations/registry?tab=vector-exports')}
          className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Vector Exports
        </button>

        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {vectorExport.display_name}
              </h1>
              {getStatusBadge(vectorExport.status)}
              {vectorExport.auto_sync && (
                <span className="px-2 py-1 text-xs font-medium bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300 rounded">
                  Auto-sync
                </span>
              )}
            </div>
            {vectorExport.description && (
              <p className="mt-1 text-gray-600 dark:text-gray-400">
                {vectorExport.description}
              </p>
            )}
            <div className="mt-3 flex items-center gap-4">
              <ProjectAssignmentSelect
                currentProjectId={assignedProjectId}
                size="md"
                onAssign={async (projectId) => {
                  await assignVectorExportProject(vectorExport.id, projectId);
                  setAssignedProjectId(projectId);
                }}
              />
              <WorkloadAssignmentSelect
                currentWorkloadId={assignedWorkloadId}
                size="md"
                onAssign={async (workloadId) => {
                  await assignVectorExportWorkload(vectorExport.id, workloadId);
                  setAssignedWorkloadId(workloadId);
                }}
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleSync}
              disabled={syncing || ['in_progress', 'syncing'].includes(vectorExport.status)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync Now'}
            </button>
            <button
              onClick={handleDeleteClick}
              disabled={deleting}
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {vectorExport.error_message && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5" />
            <div>
              <h4 className="font-medium text-red-800 dark:text-red-200">Export Error</h4>
              <p className="mt-1 text-sm text-red-700 dark:text-red-300">{vectorExport.error_message}</p>
            </div>
          </div>
        </div>
      )}

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <Database className="w-8 h-8 text-purple-600" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Source Dataset</p>
              <p className="font-medium text-gray-900 dark:text-white">
                {dataset?.name || 'Loading...'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <Cloud className="w-8 h-8 text-blue-600" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">AWS Account</p>
              <p className="font-medium text-gray-900 dark:text-white">
                {awsAccount?.account_id || 'Loading...'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <Hash className="w-8 h-8 text-green-600" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Vectors</p>
              <p className="font-medium text-gray-900 dark:text-white">
                {vectorExport.vector_count.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <Clock className="w-8 h-8 text-orange-600" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Last Sync</p>
              <p className="font-medium text-gray-900 dark:text-white">
                {formatTimestamp(vectorExport.last_sync_at)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Connection Details */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Connection Details
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <span className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
              Vector Bucket
            </span>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded text-sm text-gray-900 dark:text-white font-mono">
                {vectorExport.vector_bucket_name}
              </code>
              <button
                onClick={() => copyToClipboard(vectorExport.vector_bucket_name)}
                className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                title="Copy"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div>
            <span className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
              Vector Index
            </span>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded text-sm text-gray-900 dark:text-white font-mono">
                {vectorExport.vector_index_name}
              </code>
              <button
                onClick={() => copyToClipboard(vectorExport.vector_index_name)}
                className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                title="Copy"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div>
            <span className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
              Region
            </span>
            <code className="block px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded text-sm text-gray-900 dark:text-white font-mono">
              {vectorExport.aws_region}
            </code>
          </div>

          <div>
            <span className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
              Embedding Model
            </span>
            <code className="block px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded text-sm text-gray-900 dark:text-white font-mono">
              {vectorExport.embedding_model} ({vectorExport.embedding_dimensions} dim)
            </code>
          </div>
        </div>
      </div>

      {/* Usage Examples */}
      {vectorExport.status === 'completed' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Usage Examples
            </h2>
            <button
              onClick={() => copyToClipboard(getTabCode())}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <Copy className="w-4 h-4" />
              {copied ? 'Copied!' : 'Copy Code'}
            </button>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 dark:border-gray-700 mb-4">
            <nav className="-mb-px flex space-x-8">
              {[
                { id: 'python', label: 'Python', icon: Code },
                { id: 'cli', label: 'AWS CLI', icon: Terminal },
                { id: 'bedrock', label: 'Bedrock KB', icon: ExternalLink }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Code Block */}
          <div className="bg-gray-900 rounded-lg p-4 overflow-auto">
            <pre className="text-sm text-gray-300 whitespace-pre-wrap font-mono">
              {getTabCode()}
            </pre>
          </div>
        </div>
      )}

      {/* Delete AWS Resources Dialog */}
      <ConfirmDialog
        isOpen={deleteDialog.isOpen && deleteDialog.step === 'aws'}
        onClose={handleDeleteCancel}
        onConfirm={() => handleAwsResourcesChoice(true)}
        title="Delete AWS Resources?"
        message={
          <div>
            <p className="mb-3">
              Do you want to also delete the S3 Vectors bucket and index in your AWS account?
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              This will permanently delete the vectors from AWS. Choose "Keep AWS Resources" to only remove the export from Aivilo.
            </p>
          </div>
        }
        confirmText="Delete AWS Resources"
        cancelText="Keep AWS Resources"
        variant="danger"
      />

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={deleteDialog.isOpen && deleteDialog.step === 'confirm'}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Confirm Delete"
        message={
          <div>
            <p>
              Are you sure you want to delete "<strong>{vectorExport?.display_name}</strong>"?
            </p>
            {deleteAwsResources && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                This will also delete the S3 Vectors bucket and index from AWS.
              </p>
            )}
          </div>
        }
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={deleting}
      />
    </div>
  );
};

export default VectorExportDetailPage;
